// src/components/Sidebar.js
import React, { useRef, useEffect } from 'react';
import { Folder, X } from 'lucide-react';
import { useState } from 'react';
import NotificationBanner from './NotificationBanner';

const NewDealPopup = ({ isOpen, onClose, onSubmit }) => {
  const [dealData, setDealData] = useState({
    name: '',
    provider: '',
    folderName: ''
  });

  const providers = [
    'Google Drive',
    'SharePoint',
    'OneDrive',
    'Dropbox',
    'Box',
    'iManage',
    'NetDocuments',
    'Datasite',
    'Intralinks',
    'Merrill DatasiteOne',
    'Firmex',
    'HighQ',
    'Citrix ShareFile',
    'SecureDocs'
  ];

  const handleChange = (e) => {
    setDealData({
      ...dealData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(dealData);
    setDealData({ name: '', provider: '', folderName: '' }); // Reset form
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <h2 className="text-xl font-bold mb-6">Create New Deal</h2>
        
        <div className="text-gray-600 text-sm mb-6">
          Please go to where your deal is located, and if you can share the folder with all the deal documents, please share it to data@scaylor.com and then add the name of the folder here. If there is no way to share the specific folder and you have to share the whole deal, that is also fine. Please share it to data@scaylor.com Please do not add any spaces for the deal name.
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Deal Name</label>
              <input
                type="text"
                name="name"
                value={dealData.name}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter deal name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Data Room Provider</label>
              <select
                name="provider"
                value={dealData.provider}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                required
              >
                <option value="">Select provider</option>
                {providers.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Folder Name</label>
              <input
                type="text"
                name="folderName"
                value={dealData.folderName}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter folder name"
                required
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Create Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteDealPopup = ({ isOpen, onClose, onDelete, dealToDelete }) => {
  const [formData, setFormData] = useState({
    dealName: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  // Reset form when popup closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        dealName: '',
        email: '',
        password: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if deal name matches
    if (formData.dealName !== dealToDelete.name) {
      setError('Deal name does not match');
      return;
    }

    // Verify credentials
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      if (response.ok) {
        // If credentials are correct, proceed with deletion
        onDelete(dealToDelete.id);
        onClose();
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <h2 className="text-xl font-bold mb-6">Delete Deal</h2>
        
        <div className="text-gray-600 text-sm mb-6">
          Would you like to delete this deal? Deleting a deal is permanent. To add the deal again you will have to go through the "New Deal" process.
          <br /><br />
          If you would like to delete this deal please enter the name of the deal below alongside with your email and password
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Deal Name</label>
              <input
                type="text"
                name="dealName"
                value={formData.dealName}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter deal name to confirm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter your email"
                required
                autoComplete="off"
                autoFill="off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter your password"
                required
                autoComplete="new-password"
                autoFill="off"
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Sidebar = ({ setSelectedContextId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [deals, setDeals] = useState([]);
  const [deletedDeals, setDeletedDeals] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [isNewDealPopupOpen, setIsNewDealPopupOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [hoveredDealId, setHoveredDealId] = useState(null);
  const [notification, setNotification] = useState(null);
  const sidebarRef = useRef(null);

  const handleDealSelect = (deal) => {
    console.log('Selected deal:', deal);
    setSelectedId(deal.id);
    setSelectedContextId(deal.id);
  };

  const handleCreateDeal = async (dealData) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contexts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: dealData.name,
          cloudProvider: dealData.provider,
          sharedDirectory: dealData.folderName
        })
      });

      if (response.ok) {
        const newDeal = await response.json();
        setDeals([...deals, newDeal]);
        setIsNewDealPopupOpen(false);
      } else {
        console.error('Failed to create deal:', response.status);
      }
    } catch (err) {
      console.error('Error creating deal:', err);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    // Add the deal to deletedDeals set
    setDeletedDeals(prev => new Set([...prev, dealId]));
    
    // Clear selection if deleted deal was selected
    if (selectedId === dealId) {
      setSelectedId(null);
      setSelectedContextId(null);
    }
    
    // Show success notification
    setNotification({
      type: 'success',
      message: 'Deal has been successfully deleted'
    });
    
    // Clear notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  useEffect(() => {
    const fetchDeals = async () => {
      if (!isExpanded) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contexts`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Fetched contexts:', data); // Debug log
          setDeals(data);
        } else {
          console.error('Failed to fetch contexts:', response.status);
        }
      } catch (err) {
        console.error('Error fetching contexts:', err);
      }
    };

    fetchDeals();
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {notification && (
        <NotificationBanner
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      <div 
        ref={sidebarRef}
        className={`h-[calc(100vh-32px)] flex flex-col items-center border-r border-gray-200 bg-white shadow-[0_0_15px_rgba(0,0,0,0.1)] z-10 rounded-2xl ml-4 mr-1 mt-4 mb-4 
        ${isExpanded ? 'w-72' : 'w-24'} transition-all duration-300`}
      >
        {/* Logo at top */}
        <div className="mt-4 w-full px-4">
          <div className={`${isExpanded ? '' : 'flex justify-center'}`}>
            <span className={`font-montserrat ${
              isExpanded 
                ? 'text-sm font-bold text-black uppercase tracking-wider drop-shadow-sm' 
                : 'text-2xl font-bold'
            }`}>
              {isExpanded ? 'Scaylor' : 'S'}
            </span>
          </div>
        </div>

        {/* New Deal Button */}
        {isExpanded && (
          <div className="w-full px-4 mt-6">
            <button 
              onClick={() => setIsNewDealPopupOpen(true)}
              className="w-full py-2 px-4 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
            >
              New Deal
            </button>
          </div>
        )}

        {/* Projects section with Folder icon */}
        <div className="mt-6 w-full">
          <div className={`flex items-center w-full px-4 ${isExpanded ? 'relative' : 'justify-center'}`}>
            <Folder 
              size={20} 
              onClick={() => setIsExpanded(!isExpanded)} 
              className="cursor-pointer text-black"
            />
            {isExpanded && (
              <span className="text-[15px] font-normal text-gray-600 ml-2">
                Deals
              </span>
            )}
          </div>

          {/* Dotted line separator */}
          {isExpanded && (
            <div className="w-full px-4 my-4">
              <div className="border-t border-dashed border-gray-200"></div>
            </div>
          )}

          {/* Deals list */}
          <div className="w-full">
            {isExpanded && deals
              .filter(deal => !deletedDeals.has(deal.id))
              .map(deal => (
                <div 
                  key={deal.id}
                  className={`relative text-[14px] py-2 px-4 hover:bg-gray-50 cursor-pointer font-normal 
                    ${deal.id === selectedId ? 'text-black' : 'text-gray-600'}`}
                  onMouseEnter={() => setHoveredDealId(deal.id)}
                  onMouseLeave={() => setHoveredDealId(null)}
                >
                  <div onClick={() => handleDealSelect(deal)}>
                    {deal.name}
                  </div>
                  {hoveredDealId === deal.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDealToDelete(deal);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
                    >
                      <X size={14} className="text-gray-500" />
                    </button>
                  )}
                </div>
            ))}
          </div>
        </div>

        {/* Bottom SCAYLOR text - always visible */}
        <div className="mt-auto mb-6">
          <span className="font-montserrat text-xs font-bold text-black uppercase tracking-wider drop-shadow-sm">
            Scaylor
          </span>
        </div>
      </div>

      <NewDealPopup
        isOpen={isNewDealPopupOpen}
        onClose={() => setIsNewDealPopupOpen(false)}
        onSubmit={handleCreateDeal}
      />

      <DeleteDealPopup
        isOpen={dealToDelete !== null}
        onClose={() => setDealToDelete(null)}
        onDelete={handleDeleteDeal}
        dealToDelete={dealToDelete}
      />
    </>
  );
};

export default Sidebar;